import type { SellerProfileViewModel } from '../../../../utils/sellerProfile';
export const SELLER_REPORT_REVISION = '1.0.8';
export interface SellerReport {
  schemaVersion: 1; locale: string; timezone: string; capturedAt: string; reference: string;
  brand: { name: string; color: string; logoUrl?: string };
  seller: SellerProfileViewModel;
}
export type Translations = Record<string, string>;
export const COPY = {
  title: 'Company intelligence report', overview: 'Company overview', opportunities: 'Opportunities',
  opportunityIntro: 'Detected needs and supporting evidence for your next sales conversation.',
  business: 'Business intelligence', pains: 'Pains & priorities', brand: 'Brand & positioning',
  contacts: 'Contact channels', facts: 'Company details', highlights: 'Business at a glance',
  description: 'Campaign description', summary: 'Executive summary', evidence: 'Supporting evidence',
  value: 'Estimated value', timeline: 'Timeline', market: 'Market', decisionMaker: 'Decision maker',
  priority: 'Priority', confidence: 'Confidence', captured: 'Data as of', enriched: 'Last enriched',
  confidential: 'Confidential intelligence', empty: 'No information available yet.',
  emptyOpportunities: 'No opportunities are available for this company yet.',
};
export function validateSellerReport(value: unknown): asserts value is SellerReport {
  const d = value as SellerReport;
  if (!d || d.schemaVersion !== 1 || !/^[\w-]{1,36}$/.test(d.reference) || !Number.isFinite(Date.parse(d.capturedAt))) throw new Error('Invalid seller report identity.');
  try { new Intl.DateTimeFormat(d.locale, { timeZone: d.timezone }); } catch { throw new Error('Invalid report language or timezone.'); }
  if (!d.brand?.name || !/^#[\da-f]{6}$/i.test(d.brand.color)) throw new Error('Invalid report branding.');
  const p = d.seller;
  if (!p || p.id !== d.reference || !p.name || !['facts','contacts','highlights','business','pains','brand','opportunities'].every(k => Array.isArray(p[k as keyof typeof p]))) throw new Error('Invalid company profile.');
  const walk = (v: unknown, depth = 0) => {
    if (depth > 12) throw new Error('Company profile is too deeply nested.');
    if (typeof v === 'string' && v.length > 50000) throw new Error('Company text exceeds the report limit.');
    if (Array.isArray(v)) v.forEach(x => walk(x, depth + 1));
    else if (v && typeof v === 'object') Object.values(v).forEach(x => walk(x, depth + 1));
  };
  walk(d);
}
/** Every displayed narrative, label and evidence item participates in translation. */
export function translationTexts(d: SellerReport): Translations {
  validateSellerReport(d);
  const t: Translations = Object.fromEntries(Object.entries(COPY).map(([k,v]) => [`label.${k}`,v]));
  const p = d.seller;
  for (const k of ['industry','location','summary','description','brandNarrative'] as const) if (p[k]) t[`company.${k}`] = p[k];
  p.facts.forEach((f,i) => { t[`fact.${i}.label`] = f.label; t[`fact.${i}.value`] = f.value; });
  p.contacts.forEach((c,i) => { t[`contact.${i}.label`] = c.label; });
  const groups = (gs: typeof p.highlights, prefix: string) => gs.forEach((g,i) => { t[`${prefix}.${i}.label`] = g.label; g.items.forEach((v,j) => { t[`${prefix}.${i}.item.${j}`] = v; }); });
  groups(p.highlights, 'highlights');
  for (const kind of ['business','pains','brand'] as const) p[kind].forEach((s,i) => { t[`${kind}.${i}.title`] = s.title; groups(s.groups, `${kind}.${i}.group`); });
  p.opportunities.forEach((o,i) => {
    for (const k of ['title','need','urgency','amount','timeline','country','confidence','decisionMaker'] as const) if (o[k]) t[`opp.${i}.${k}`] = o[k];
    o.evidence.forEach((v,j) => { t[`opp.${i}.evidence.${j}`] = v; });
  });
  return t;
}
export function validateTranslations(d: SellerReport, value: unknown): asserts value is Translations {
  const source = translationTexts(d); const t = value as Translations;
  if (!t || Object.keys(t).length !== Object.keys(source).length || !Object.keys(source).every(k => typeof t[k] === 'string' && (!source[k].trim() || t[k].trim()))) throw new Error('Translation is incomplete. Every company text and evidence item is required.');
}
